import { generatePath } from 'react-router';
import { TableUrlParams } from '../../components/Table/tableConstants.js';
import { paths } from '../constants/pathVariables.js';
import { DeviceSelectionProductTypeCoCo } from '../enums.js';
import { elisaDevicesServiceMsg, purchasedDeviceMsg, t } from '../i18n';
import type { ShouldRevalidateFunctionArgs } from 'react-router';

export { DeviceSelectionProductTypeCoCo } from '../enums.js';

export const DEVICE_LIST_TAB_PARAM = 'tab';
export const MDM_ID_PARAM = 'mdmId';

// Own URL parameter so that the Device lists tab's productType filter doesn't leak into the Device selection tab
export const DEVICE_SELECTION_PRODUCT_TYPE_PARAM = 'deviceSelectionProductType';

export const DEVICE_LIST_PRODUCT_TYPE_PARAM = 'productType';
export const DEVICE_LIST_CONTRACT_PERIOD_PARAM = 'contractPeriod';
export const DEVICE_LIST_STATUS_PARAM = TableUrlParams.STATUS;

// Multiple selected filter values are separated with '|' in the URL, as in the other OmaElisa list filters
export const FILTER_VALUE_SEPARATOR = '|';

export const DEVICE_SELECTION_PRODUCT_TYPES = [
  DeviceSelectionProductTypeCoCo.EPP,
  DeviceSelectionProductTypeCoCo.PURCHASED,
];

export const getDeviceSelectionProductTypeLabel = (productType: DeviceSelectionProductTypeCoCo) =>
  productType === DeviceSelectionProductTypeCoCo.EPP ? t.TRE5(elisaDevicesServiceMsg) : t.QL8F(purchasedDeviceMsg);

// Values are used in the URL
export enum DeviceListTabCoCo {
  DEVICE_LISTS = 'laitelistat',
  DEVICE_SELECTION = 'laitevalikoima',
}

// Search, filter and paging params are tab specific, so they are cleared when switching to the Device selection tab
export const TAB_SPECIFIC_URL_PARAMS = [
  TableUrlParams.SEARCH,
  TableUrlParams.SORT,
  TableUrlParams.ORDER,
  TableUrlParams.OFFSET,
  TableUrlParams.LIMIT,
  TableUrlParams.PAGE,
  DEVICE_LIST_STATUS_PARAM,
  DEVICE_LIST_PRODUCT_TYPE_PARAM,
  DEVICE_LIST_CONTRACT_PERIOD_PARAM,
  DEVICE_SELECTION_PRODUCT_TYPE_PARAM,
];

export const getSelectedDeviceListTab = (tabParam?: string | null): DeviceListTabCoCo =>
  tabParam === DeviceListTabCoCo.DEVICE_SELECTION ? DeviceListTabCoCo.DEVICE_SELECTION : DeviceListTabCoCo.DEVICE_LISTS;

export const deviceSelectionTabPath = `${paths.COMPANY_INFO_CATALOGS}?${DEVICE_LIST_TAB_PARAM}=${DeviceListTabCoCo.DEVICE_SELECTION}`;

export const withMdmIdParam = (path: string, mdmId?: string) =>
  mdmId ? `${path}?${new URLSearchParams({ [MDM_ID_PARAM]: mdmId })}` : path;

export const getDeviceListsTabPath = (mdmId: string) => withMdmIdParam(paths.COMPANY_INFO_CATALOGS, mdmId);

export const getNewDeviceListPath = (mdmId?: string) => withMdmIdParam(paths.COMPANY_INFO_CATALOG_LIST_NEW, mdmId);

export const getNewDeviceSelectionPath = (mdmId?: string) =>
  withMdmIdParam(paths.COMPANY_INFO_DEVICE_SELECTION_NEW, mdmId);

export const getDeviceSelectionPath = (uuid: string, mdmId?: string) =>
  withMdmIdParam(generatePath(paths.COMPANY_INFO_DEVICE_SELECTION, { deviceSelectionUuid: uuid }), mdmId);

export const getDeviceListPath = (uuid: string, mdmId?: string) =>
  withMdmIdParam(generatePath(paths.COMPANY_INFO_DEVICE_LIST, { deviceListUuid: uuid }), mdmId);

export const getEditDeviceListPath = (uuid: string, mdmId?: string) =>
  withMdmIdParam(generatePath(paths.COMPANY_INFO_DEVICE_LIST_EDIT, { deviceListUuid: uuid }), mdmId);

export const getEditDeviceSelectionPath = (uuid: string, mdmId?: string) =>
  withMdmIdParam(generatePath(paths.COMPANY_INFO_DEVICE_SELECTION_EDIT, { deviceSelectionUuid: uuid }), mdmId);

export const toPathWithQuery = (pathname: string, query: string, hash = '') =>
  pathname + (query ? `?${query}` : '') + hash;

const withoutTabSpecificParams = (search: string) => {
  const queryParams = new URLSearchParams(search);
  TAB_SPECIFIC_URL_PARAMS.forEach(param => queryParams.delete(param));
  return queryParams;
};

export const getDeviceSelectionTabQuery = (search: string): string => {
  const queryParams = withoutTabSpecificParams(search);
  queryParams.set(DEVICE_LIST_TAB_PARAM, DeviceListTabCoCo.DEVICE_SELECTION);
  return queryParams.toString();
};

// Restores the previous Device lists query if there is one, so that search, filters and paging are kept
export const getDeviceListsTabQuery = (search: string, loadedQuery?: string): string => {
  if (loadedQuery !== undefined) {
    return loadedQuery;
  }
  const queryParams = withoutTabSpecificParams(search);
  queryParams.delete(DEVICE_LIST_TAB_PARAM);
  return queryParams.toString();
};

const getTabFromUrl = (url: URL) => getSelectedDeviceListTab(url.searchParams.get(DEVICE_LIST_TAB_PARAM));

export const getDeviceListsQuery = (search: string): string => {
  const queryParams = new URLSearchParams(search);
  queryParams.delete(DEVICE_LIST_TAB_PARAM);
  queryParams.sort();
  return queryParams.toString();
};

// Query of the currently loaded device lists data. Kept up to date by DeviceListTabsCoCo while the Device lists tab is
// shown, so that returning to the tab with the same query can reuse the already loaded data.
let loadedDeviceListsQuery: string | undefined;

export const getLoadedDeviceListsQuery = () => loadedDeviceListsQuery;

export const setLoadedDeviceListsQuery = (query: string | undefined) => {
  loadedDeviceListsQuery = query;
};

// Switching between the tabs must not refetch device lists: the data isn't needed on the Device selection tab, and
// when returning to the Device lists tab its previous query is restored, which matches the already loaded data.
export const shouldRevalidateDeviceLists = ({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs): boolean => {
  if (currentUrl.pathname !== nextUrl.pathname) {
    return defaultShouldRevalidate;
  }
  if (getTabFromUrl(nextUrl) === DeviceListTabCoCo.DEVICE_SELECTION) {
    return false;
  }
  if (
    getTabFromUrl(currentUrl) === DeviceListTabCoCo.DEVICE_SELECTION &&
    loadedDeviceListsQuery !== undefined &&
    getDeviceListsQuery(nextUrl.search) === loadedDeviceListsQuery
  ) {
    return false;
  }
  return defaultShouldRevalidate;
};

export const shouldRevalidateDeviceListTabs = ({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs): boolean => {
  if (currentUrl.pathname !== nextUrl.pathname || currentUrl.href === nextUrl.href) {
    return defaultShouldRevalidate;
  }
  if (
    getTabFromUrl(currentUrl) !== getTabFromUrl(nextUrl) ||
    currentUrl.searchParams.get(MDM_ID_PARAM) !== nextUrl.searchParams.get(MDM_ID_PARAM)
  ) {
    return true;
  }
  // Search, filters and sorting are applied to the already loaded data
  return false;
};
